<template>
  <div
    v-if="spa"
    class="spa-detail"
    itemscope
    itemtype="https://schema.org/DaySpa"
  >
    <img
      class="util-snippet"
      itemprop="image"
      alt="Product Image"
      :src="firstImage"
    >

    <Breadcrumb :name-override-stack="[spa.name]" />

    <div class="spa-detail__header-wrapper">
      <Card
        mobile-with-border
        class="spa-detail__card"
      >
        <div class="spa-detail__header">
          <div class="spa-detail__header__score">
            <LeafScoreMeterAtom :score="score" />
          </div>
          <div
            v-if="score"
            property="aggregateRating"
            itemprop="aggregateRating"
            itemscope
            itemtype="https://schema.org/AggregateRating"
          >
            <span
              class="util-snippet"
              itemprop="ratingValue"
            >{{ score }}</span>
            <span

              class="util-snippet"
              itemprop="bestRating"
            >{{ maxScore }}</span>
            <span
              class="util-snippet"
              itemprop="reviewCount"
            >{{ reviewCount }}</span>
          </div>
          <div class="spa-detail__header__spa-name">
            <div class="spa-detail__header__icon">
              <PlantIcon />
            </div>
            <h1 itemprop="name">
              {{ spa.name }}
            </h1>
          </div>
        </div>

        <div class="spa-detail__location">
          <HotelNameStarsAtom
            v-if="spa.hotel"
            :hotel="spa.hotel"
          />
          <div>
            <LocationLink
              :location="spa.location"
              show-icon
              display-full-address
            />
          </div>
        </div>

        <GalleryMolecule
          :images="spa.images"
          orientation="landscape"
          class="spa-detail__gallery"
        >
          <Badge
            v-show="spa.status !== 'online'"
            color="acent"
            class="service-detail-page__header__spa-status"
          >
            <SpaStatusLabel :spa-status="spa.status" />
          </Badge>
        </GalleryMolecule>
      </Card>
    </div>

    <div>
      <div
        property="description"
        itemprop="description"
        class="spa-detail__info"
      >
        <div>
          <SectionAtom
            :start-collapsed="true"
            :is-collapsable="true"
            :html="longDescription"
          >
            <template #title>
              <p class="spa-detail__info__description__title">
                <Lang
                  by-key="description"
                  capitalize-first
                />
              </p>
            </template>
          </SectionAtom>
        </div>
      </div>
    </div>

    <div class="spa-detail__services-getaways-container">
      <div
        v-if="getAways.length && getAwaysByHotelGroup"
        class="spa-detail__get-aways"
      >
        <p class="spa-detail__get-aways__title">
          <Lang
            by-key="escapada"
            plural
            capitalize-first
          /> <Lang
            by-key="hotel-and-spa"
            capitalize-all
          />
        </p>

        <div class="spa-detail__get-aways__body">
          <div>
            <Card
              class="spa-detail__get-aways__body__filters"
              desktop-with-border
            >
              <p class="spa-detail__get-aways__body__filters__title">
                <Lang
                  by-key="search-by-date"
                  capitalize-first
                />
              </p>

              <div class="spa-detail__get-aways__body__filters__dates">
                <DateRangePicker variant="minimized" />
              </div>

              <div class="spa-detail__get-aways__body__filters__pax">
                <Tooltip
                  icon-variant="semi-light"
                  translatable-content-key="get-away-if-you-want-more-people"
                />
                <div>
                  <Lang
                    by-key="search-for-two"
                    capitalize-first
                  />
                </div>
              </div>

              <div class="spa-detail__get-aways__body__filters__update-btn">
                <SplButton
                  :disabled="!hasValidDateRange"
                  variant="acent"
                  size="desk-full-mobile-small"
                  @click="fetchGetAwayAvailability()"
                >
                  <Lang
                    by-key="search"
                    capitalize-first
                  />
                </SplButton>
              </div>
            </Card>
          </div>
          <div>
            <GetAwayListItemByHotel
              show-availability-dates
              :availability-from="fetchedAvailabilityDateFrom"
              :availability-to="fetchedAvailabilityDateTo"
              :get-away-group-by-hotel="getAwaysByHotelGroup"
              :treatments="availableTreatmentsForGetAways"
            />
          </div>
        </div>

        <Separator />
      </div>

      <!-- Service list -->
      <div>
        <div class="spa-detail__service-list__section-title">
          <p>
            <Lang
              by-key="services-and-treatments"
              capitalize-all-large
            />
          </p>
        </div>

        <div class="spa-detail__list-wrapper">
          <!-- filtros -->
          <div>
            <div class="only-desktop-block">
              <Card
                desktop-with-border
                class="spa-detail__filters"
              >
                <div class="spa-detail__filters__title">
                  <Lang
                    by-key="filter-by"
                    capitalize-first
                  />
                </div>

                <div
                  v-if="promoCategoryNodes.length"
                  class="spa-detail__filters__expanded-filter"
                >
                  <p class="spa-detail__filters__expanded-filter__header">
                    <Lang
                      by-key="promotion"
                      plural
                      capitalize-all
                    />
                  </p>

                  <TruncatedList
                    :split-at="10"
                    :data="promoCategoryNodes"
                    expand-button-left
                  >
                    <template #default="{ element }">
                      <div
                        class="spa-detail__filters__expanded-filter__checkbox-node"
                        :class="{'selected': element.value}"
                      >
                        <input
                          :id="`category-checkbox-${element.id}`"
                          v-model="element.value"
                          type="checkbox"
                          @change="onPromoCategoryNodeClick"
                        >
                        <label :for="`category-checkbox-${element.id}`">
                          <Lang
                            :by-raw="element.texts"
                            capitalize-all
                          />
                        </label>
                      </div>
                    </template>
                  </TruncatedList>
                  <hr>
                </div>

                <div
                  v-if="treatmentCategoryNodes.length"
                  class="spa-detail__filters__expanded-filter"
                >
                  <p class="spa-detail__filters__expanded-filter__header">
                    <Lang
                      by-key="treatment"
                      capitalize-first
                      plural
                    />
                  </p>

                  <TruncatedList
                    :split-at="10"
                    :data="treatmentCategoryNodes"
                    expand-button-left
                  >
                    <template #default="{ element }">
                      <div
                        class="spa-detail__filters__expanded-filter__checkbox-node"
                        :class="{'selected': element.value}"
                      >
                        <input
                          :id="`category-checkbox-${element.id}`"
                          v-model="element.value"
                          type="checkbox"
                          @change="onTreatmentCategoryNodeClick"
                        >
                        <label :for="`category-checkbox-${element.id}`">
                          <Lang
                            :by-raw="element.texts"
                            capitalize-all
                          />
                        </label>
                      </div>
                    </template>
                  </TruncatedList>
                  <hr>
                </div>

                <div class="spa-detail__filters__expanded-filter">
                  <p class="spa-detail__filters__expanded-filter__header">
                    <Lang
                      by-key="person"
                      capitalize-first
                      plural
                    />
                  </p>

                  <PaxInlineSelector />
                </div>
              </Card>
            </div>
            <div class="only-mobile">
              <div class="spa-detail__mobile-filters">
                <div class="spa-detail__mobile-filters">
                  <StickyHeader>
                    <template #only-sticky>
                      <div class="spa-detail__mobile-filters__header__title">
                        {{ spa.name }}
                      </div>
                    </template>
                    <template #body>
                      <div class="spa-detail__mobile-filters__header">
                        <div class="spa-detail__mobile-filters__header__controls">
                          <button
                            class="spa-detail__mobile-filters__button"
                            @click="setIsModalDisplayed(true)"
                          >
                            <HopperIcon
                              class="spa-detail__mobile-filters__button__icon"
                              :size="18"
                            />
                            <span><Lang
                              by-key="filter"
                              capitalize-first
                            /></span>
                          </button>

                          <SelectBox
                            id="spa-detail-sorter"
                            variant="minified"
                            :data="sortOptions"
                            display-prop="label"
                            class="spa-detail__mobile-filters__sort-selector"
                            @input="pushSortOrder"
                          >
                            <template #label>
                              <Lang
                                by-key="sort-by"
                                capitalize-first
                              />: <span class="spa-detail__mobile-filters__sort-selector__label">{{ selectedSortOptionLabel }}</span>
                            </template>
                          </SelectBox>
                        </div>

                        <div class="spa-detail__mobile-selected-filters">
                          <div
                            v-if="selectedPromos.length"
                            class="spa-detail__mobile-selected-filters__row"
                          >
                            <Badge
                              v-for="category in selectedPromos"
                              :key="category.id"
                              class="spa-detail__mobile-selected-filters__category"
                            >
                              {{ category.label }}
                            </Badge>
                          </div>

                          <div
                            v-if="selectedTreatments.length"
                            class="spa-detail__mobile-selected-filters__row"
                          >
                            <Badge
                              v-for="category in selectedTreatments"
                              :key="category.id"
                              color="secondary"
                              class="spa-detail__mobile-selected-filters__category"
                            >
                              <Lang
                                :by-raw="category.texts"
                                capitalize-all
                              />
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </template>
                  </StickyHeader>

                  <ConfigModalMobile
                    v-model="isModalDisplayed"
                    is-apply-button-displayed
                  >
                    <template #header>
                      <div
                        class="spa-list__mobile-filters__header"
                      >
                        <Lang
                          by-key="filter-by"
                          capitalize-first
                        />
                      </div>
                    </template>

                    <div
                      v-if="promoCategoryNodes.length"
                      class="spa-detail__mobile-filters__expanded-filter"
                    >
                      <p class="spa-detail__mobile-filters__expanded-filter__header">
                        <Lang
                          by-key="promotion"
                          plural
                          capitalize-all
                        />
                      </p>

                      <TruncatedList
                        :split-at="10"
                        :data="promoCategoryNodes"
                        expand-button-left
                      >
                        <template #default="{ element }">
                          <div
                            class="spa-detail__filters__expanded-filter__checkbox-node"
                            :class="{'selected': element.value}"
                          >
                            <input
                              :id="`category-checkbox-${element.id}`"
                              v-model="element.value"
                              type="checkbox"
                              @change="onPromoCategoryNodeClick"
                            >
                            <label :for="`category-checkbox-${element.id}`">
                              <Lang
                                :by-raw="element.texts"
                                capitalize-all
                              />
                            </label>
                          </div>
                        </template>
                      </TruncatedList>
                      <hr>
                    </div>

                    <div
                      v-if="treatmentCategoryNodes.length"
                      class="spa-detail__mobile-filters__expanded-filter"
                    >
                      <p class="spa-detail__mobile-filters__expanded-filter__header">
                        <Lang
                          by-key="treatment"
                          capitalize-first
                          plural
                        />
                      </p>

                      <TruncatedList
                        :split-at="10"
                        :data="treatmentCategoryNodes"
                        expand-button-left
                      >
                        <template #default="{ element }">
                          <div
                            class="spa-detail__filters__expanded-filter__checkbox-node"
                            :class="{'selected': element.value}"
                          >
                            <input
                              :id="`category-checkbox-${element.id}`"
                              v-model="element.value"
                              type="checkbox"
                              @change="onTreatmentCategoryNodeClick"
                            >
                            <label :for="`category-checkbox-${element.id}`">
                              <Lang
                                :by-raw="element.texts"
                                capitalize-all
                              />
                            </label>
                          </div>
                        </template>
                      </TruncatedList>
                      <hr>
                    </div>

                    <div class="spa-detail__mobile-filters__expanded-filter">
                      <p class="spa-detail__mobile-filters__expanded-filter__header">
                        <Lang
                          by-key="person"
                          capitalize-first
                          plural
                        />
                      </p>

                      <PaxInlineSelector />
                    </div>
                  </ConfigModalMobile>
                </div>
              </div>
            </div>
          </div>

          <div class="spa-detail__service-list">
            <div class="spa-detail__service-list__header">
              <div>
                <h2 class="spa-detail__service-list__header__subtitle">
                  {{ subtitle }}
                </h2>
              </div>

              <div>
                <SelectBox
                  id="spa-detail-sorter"
                  variant="minified"
                  :data="sortOptions"
                  display-prop="label"
                  class="spa-detail__service-list__header__sort-selector"
                  @input="pushSortOrder"
                >
                  <template #label>
                    <Lang
                      by-key="sort-by"
                      capitalize-first
                    />: <span class="spa-detail__service-list__header__sort-selector__label">
                      {{ selectedSortOptionLabel }}
                    </span>
                  </template>
                </SelectBox>
              </div>
            </div>

            <!-- Service list -->
            <div v-if="servicesView.length">
              <TruncatedList
                :split-at="24"
                :data="servicesView"
                desktop-table-col-2
              >
                <template #default="{ element }">
                  <ServiceListItem
                    :service="element"
                    :spa="spa"
                    :selected-pax="selectedPax"
                    :has-selected-pax="hasSelectedPax"
                  />
                </template>
              </TruncatedList>
            </div>

            <div v-else>
              <div
                class="spa-detail__service-list__no-services"
              >
                <p class="spa-detail__service-list__no-services__header">
                  <Lang
                    by-key="no-available-services"
                    capitalize-first
                  />
                </p>
                <p>
                  <Lang
                    by-key="no-available-services-secondary"
                    capitalize-first
                  />
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator />
      </div>
    </div>

    <div>
      <BenefitsCard
        :tags="['BEST_PRICE', 'GIVINGS', 'SAFE_PAYMENT']"
        :can-close="false"
      />

      <Separator />
    </div>

    <div id="spa-map" />

    <SpaMapAndSchedule :spa="spa" />

    <Separator />

    <SpaReviewMaster :spa-uuid="spa.uuid" />
  </div>
</template>

<script lang="ts">
import Card from '~/core/components/shared/Card.vue'
import Badge from '~/core/components/shared/Badge.vue'
import SectionAtom from '~/core/components/shared/Section.atom.vue'
import GalleryMolecule from '~/core/components/shared/gallery/Gallery.molecule.vue'
import Separator from '~/core/components/shared/Separator.vue'
import TruncatedList from '~/core/components/shared/TruncatedList.vue'
import BenefitsCard from '~/core/components/spa/BenefitsCard.vue'
import GMap from '~/core/components/shared/GMap.vue'
import SpaMapAndSchedule from '~/core/components/spa/SpaMapAndSchedule.vue'

import LeafScoreMeterAtom from '~/core/components/spa/atom/LeafScoreMeter.atom.vue'
import HotelNameStarsAtom from '~/core/components/hotel/atom/HotelNameStars.atom.vue'
import LocationLink from '~/core/components/shared/LocationLink.vue'
import Breadcrumb from '~/core/components/shared/Breadcrumb.vue'
import ServiceListItem from '~/core/components/spa-service/atom/ServiceListItem.vue'
import PaxInlineSelector from '~/core/components/shared/form/PaxInlineSelector.vue'

import ConfigModalMobile from '~/core/components/shared/form/ConfigModalMobile.vue'
import SelectBox from '~/core/components/shared/form/SelectBox.vue'
import SplNuxtLink from '~/core/components/shared/SplNuxtLink.vue'
import SplButton from '~/core/components/shared/form/SplButton.vue'
import DateRangePicker from '~/core/components/shared/form/DateRangePicker.vue'
import Tooltip from '~/core/components/shared/Tooltip.vue'

import SpaReviewMaster from '~/core/components/spa-review/molecules/SpaReviewMaster.vue'
import StickyHeader from '~/core/components/shared/StickyHeader.vue'
import GetAwayListItemByHotel from '~/core/components/get-away/GetAwayListItemByHotel.vue'
import SpaStatusLabel from '~/core/components/spa/SpaStatusLabel.vue'

import PlantIcon from '~/core/components/shared/icons/Plant.icon.vue'
import HopperIcon from '~/core/components/shared/icons/Hopper.icon.vue'

import config from '~/core/ts/server-config'

import { translate, translateByKey } from '~/core/ts/util/translate'
import { joinPaths, capitalizeAllWords, capitalizeFirstWord } from '~/core/ts/util/string'

import { getSpaDetailPageMetadata } from '~/core/composable/spa/useSpaDetailPageHeader'
import SpaNavigationStack from '~/core/ts/repository/SpaNavigationStackRepository'
import { GetAwayGroupByHotel } from '~/core/ts/entity/GetAwayGroupByHotel'
import { GetAwayWithAvailability } from '~/core/ts/entity/GetAwayWithAvailability'

import { useLocation } from '~/core/composable/shared/useLocation'
import { usePaxSelector } from '~/core/composable/spa-service/usePaxSelector'
import { useSorter } from '~/core/composable/spa/useSorter'
import { useMultiPromoFilter } from '~/core/composable/spa/useMultiPromoFilter'
import { useMultiTreatmentFilter } from '~/core/composable/spa/useMultiTreatmentFilter'
import { useFetchProxy } from '~/core/composable/shared/useFetchProxy'
import { useDateRange } from '~/core/composable/shared/useDateRange'
import { useGetAwayAvailability } from '~/core/composable/get-away/useGetAwayAvailability'
import { useLangSelector } from '~/core/composable/shared/useLangSelector'

import { Spa } from '~/core/ts/entity/Spa'
import { Treatment } from '~/core/ts/entity/Treatment'
import { CheckBoxNode } from '~/core/ts/entity/CheckBoxNode'
import { Promotion } from '~/core/ts/entity/Promotion'
import { ServiceCollection } from '~/core/ts/entity/Service'
import { GetAway } from '~/core/ts/entity/GetAway'
import type { Tag } from '~/core/ts/entity/Tag'
import LoadingEventBus from '~/core/events/loading'

export default defineNuxtComponent({
  components: {
    Card,
    Badge,
    SectionAtom,
    GalleryMolecule,
    Separator,
    TruncatedList,
    BenefitsCard,
    GMap,
    SpaMapAndSchedule,
    LeafScoreMeterAtom,
    HotelNameStarsAtom,
    LocationLink,
    Breadcrumb,
    StickyHeader,
    GetAwayListItemByHotel,
    SpaStatusLabel,
    ServiceListItem,
    ConfigModalMobile,
    SelectBox,
    PaxInlineSelector,
    SplNuxtLink,
    SplButton,
    DateRangePicker,
    Tooltip,
    SpaReviewMaster,
    PlantIcon,
    HopperIcon,
  },
  async setup() {
    const route = useRoute()
    const runtimeConfig = useRuntimeConfig()
    const domain = runtimeConfig.public.domain

    // Filters vars
    const isModalDisplayed = ref(false)
    function setIsModalDisplayed(value: boolean) {
      isModalDisplayed.value = value
    }

    const {
      selectedPax,
      hasSelectedPax,
    } = usePaxSelector(ref([]), '0')

    const {
      currentLang
    } = useLangSelector()

    const recommendedText = capitalizeAllWords(translateByKey('recommended', true))
    const cheapestServiceText = capitalizeAllWords(translateByKey('cheapest-service'))
    const mostExpensiveServiceText = capitalizeAllWords(translateByKey('most-expensive-service'))

    const {
      sortOrder,
      pushSortOrder,
      sortOptions,
      selectedSortOptionLabel
    } = useSorter([
      { label: cheapestServiceText, value: 'cheapest' },
      { label: mostExpensiveServiceText, value: 'more_expensive' },
      { label: recommendedText, value: 'recommended' },
    ], 'cheapest')

    const promoCategoryNodes = ref<CheckBoxNode[]>([])
    const {
      selectedPromos,
      onPromoCategoryNodeClick,
    } = useMultiPromoFilter(promoCategoryNodes)

    const treatmentCategoryNodes = ref<CheckBoxNode[]>([])
    const {
      selectedTreatments,
      onTreatmentCategoryNodeClick,
    } = useMultiTreatmentFilter(treatmentCategoryNodes)

    const {
      fetchedAvailabilityDateFrom,
      fetchedAvailabilityDateTo,

      generateNextWeekEndFromAndToDates,

      getAvailabilityByUUIDs,
      getAvailabilityOf,
    } = useGetAwayAvailability()

    const {
      hasValidDateRange,

      selectedDateFrom,
      selectedDateTo,

      pushDateRangeInput,
    } = useDateRange()

    async function fetchGetAwayAvailability() {
      LoadingEventBus.show()
      if (hasValidDateRange.value) {
        await getAvailabilityByUUIDs(
          getAways.map((ga) => ga.uuid),
          selectedDateFrom.value as Date,
          selectedDateTo.value as Date,
          2
        )
      }
      LoadingEventBus.hide()
    }

    onMounted(() => {
      if (spa) {
        setTimeout(() => SpaNavigationStack.push(spa), 200)
      }

      if (!getAways.length) {
        return
      }

      if (!hasValidDateRange.value) {
        setTimeout(() => {
          const { from, to } = generateNextWeekEndFromAndToDates()
          pushDateRangeInput(from, to)
        }, 500)
      }

      setTimeout(() => {
        if (hasValidDateRange.value) {
          getAvailabilityByUUIDs(
            getAways.map((ga) => ga.uuid),
            selectedDateFrom.value as Date,
            selectedDateTo.value as Date,
            2
          )
        }
      }, 800)
    })

    // End Filters vars

    const uri = route.path
    const spa = await useFetchProxy<Spa>(
      '/api/spas/spa-by-uri',
      {
        method: 'post',
        body: { uri },
      })

    const getAways: GetAway[] = await useFetchProxy<GetAway[]>(
      '/api/getaway/get-getaway-list-by-spa-uuid',
      {
        method: 'post',
        body: { spaUUID: spa.uuid },
        key: spa.uuid
      })

    const getAwaysByHotelGroup = computed(() => {
      if (!getAways.length) {
        return null
      }

      const getAwaysWithAvailability = getAways.map((ga) => {
        return GetAwayWithAvailability.from(ga, getAvailabilityOf(ga.uuid))
      })

      const group = GetAwayGroupByHotel.fromSameSpaGetAways(getAwaysWithAvailability)
      return group
    })

    // Process services to fill promoCategoryNodes
    const extraPromosDict: Record<string, Tag> = {}
    spa.services.forEach((service) => {
      service.promotionTags.forEach((serviceTag) => {
        extraPromosDict[serviceTag.uuid] = serviceTag
      })
    })
    promoCategoryNodes.value = Promotion.filterByActiveFest(Object.values(extraPromosDict))
      .sort((promoA, promoB) => promoA.title.localeCompare(promoB.title))
      .map((promotionTag) => {
        const texts: CheckBoxNode['texts'] = {}
        Object.entries(promotionTag.texts).forEach(([key, value]) => texts[key] = value.title)

        return CheckBoxNode.copy({
          id: promotionTag.uuid,
          label: capitalizeAllWords(promotionTag.title),
          value: false,
          texts,
          metadata: { promotionTag }
        })
      })

    // Process services to fill treatment options
    const treatments = await useFetchProxy<Array<Treatment>>('/api/treatments/treatment-list', {})
    treatmentCategoryNodes.value = treatments
      .sort((treatmentA, treatmentB) => treatmentA.title.localeCompare(treatmentB.title))
      .filter((treatment) => {
        const collection = new ServiceCollection(spa.services)
        collection.filterByTreatmentUuid(treatment.uuid)

        return !!collection.services.length
      })
      .map((treatment) => {
        const texts: CheckBoxNode['texts'] = {}
        Object.entries(treatment.texts).forEach(([key, value]) => texts[key] = value.title)

        return CheckBoxNode.copy({
          id: treatment.uuid,
          label: capitalizeAllWords(treatment.title),
          value: false,
          texts,
          metadata: { treatment }
        })
      })

    const availableTreatmentsForGetAways = (() => {
      const treatmentsByUUID: Record<string, Treatment> = {}
      treatments.forEach((treatment) => treatmentsByUUID[treatment.uuid] = treatment)

      let tagUUIDs: Array<string> = []
      getAways.forEach((getAway) => {
        const uuids = GetAway.getTagUUIDs(getAway)
        tagUUIDs = tagUUIDs.concat(uuids)
      })

      return tagUUIDs
        .map((uuid) => treatmentsByUUID[uuid])
        .filter((treatment) => !!treatment)
    })()

    const firstImage = spa.images.length ? spa.images[0].url : ''


    // --- Listado de servicios ---
    const servicesView = computed(() => {
      const collection = new ServiceCollection(spa.services.slice())

      // Filtro por pareja/no pareja
      if (selectedPax.value.value !== '0') {
        const pax = Number(selectedPax.value.value)
        collection.filterByPaxValue(pax)
      }

      // Filtro por promo categorías
      const categoryUUIDs = selectedPromos.value.map((node) => node.id)
      collection.filterByPromoCategoryUuids(categoryUUIDs)

      // Filtro por tratamiento
      if (selectedTreatments.value.length) {
        const uuids = selectedTreatments.value.map((node) => node.id)
        collection.filterByTreatmentUuids(uuids)
      }

      switch (sortOrder.value.value) {
        case 'cheapest':
          collection.sortByCheaper(new Date())
          break
        case 'more_expensive':
          collection.sortByMostExpensive(new Date())
          break
        case 'recommended':
          collection.sortByRecommended()
          break
      }

      if (config.promos.isValentines) {
        const valentines = Promotion.getValentines(currentLang)
        collection.sortByPromotion(valentines)
      }

      if (config.promos.isBlackFriday) {
        const blackFriday = Promotion.getBlackFriday()
        collection.sortByPromotion(blackFriday)
      }

      if (config.promos.isFathersDay) {
        collection.sortByPromotionUUID(Promotion.fathersDayUUID)
      }

      if (config.promos.isMothersDay) {
        collection.sortByPromotionUUID(Promotion.getMothersDay().uuid)
      }

      return collection.services
    })
    // --- Final listado de servicios ---

    const foundTexts = capitalizeFirstWord(translateByKey('found', false))
    const foundTextp = capitalizeFirstWord(translateByKey('found', true))

    const serviceTexts = capitalizeFirstWord(translateByKey('service', false))
    const serviceTextp = capitalizeFirstWord(translateByKey('service', true))

    const subtitle = computed(() => {
      if (servicesView.value.length === 1) {
        return `${foundTexts} 1 ${serviceTexts}`
      }

      return `${foundTextp} ${servicesView.value.length} ${serviceTextp}`
    })

    // Textos
    const longDescription = translate(spa.texts).longDescription

    // Valores para los Richsnippets de Google
    // NOTE: Recomendación de David Rodríguez. Si no ha nota que mostrar, ponemos 4 por defecto.
    const score = spa.score ? spa.score : 4
    const maxScore = 5
    const reviewCount = 1

    const { locationLabel, externalMapLink } = useLocation(spa.location)

    const url = joinPaths(domain, uri)
    useHead(getSpaDetailPageMetadata(spa, url))

    return {
      spa,
      locationLabel,
      externalMapLink,
      subtitle,

      longDescription,

      firstImage,
      score,
      maxScore,
      reviewCount,
      servicesView,

      // Escapadas
      getAways,
      hasValidDateRange,
      fetchedAvailabilityDateFrom,
      fetchedAvailabilityDateTo,
      getAvailabilityOf,
      fetchGetAwayAvailability,

      getAwaysByHotelGroup,
      availableTreatmentsForGetAways,

      // Filters
      isModalDisplayed,
      setIsModalDisplayed,

      // Filters pax
      selectedPax,
      hasSelectedPax,

      // Filters order
      sortOrder,
      pushSortOrder,
      sortOptions,
      selectedSortOptionLabel,

      // Promo Category filter
      selectedPromos,
      onPromoCategoryNodeClick,
      promoCategoryNodes,

      // Treatment filter
      treatmentCategoryNodes,
      selectedTreatments,
      onTreatmentCategoryNodeClick,
    }
  }
})
</script>

<style lang="scss" scoped>
.spa-detail {
  &__header-wrapper {
    @apply mb-2;
  }

  &__card {
    @apply my-4;
    @apply mx-1;

    @apply py-2;
  }

  &__header {
    @apply mt-1 mb-1;

    @screen md {
      @apply flex flex-row-reverse;
      @apply justify-between items-center;
    }

    &__score {
      @apply flex flex-row-reverse;

      @apply mt-1;
      @apply mb-2;
      @apply px-2;
    }

    &__spa {
      &-name {
        @apply text-gray-800;
        @apply mb-3;
        @apply font-bold;
        font-size: 21px;
        @apply flex items-center;
      }
    }

    &__icon {
      min-width: 40px;
      width: 40px;
      height: 40px;
      @apply mr-2;
      @apply rounded-full;
      background: #017074;
      @apply flex items-center justify-center;
    }
  }

  &__location {
    @apply mx-4;
    @apply mt-8 mb-4;
    @apply text-gray-900;

    @screen lg {
      @apply mt-0;
      @apply mx-0;

      @apply mb-3;
    }

    &__address {
      @apply underline italic;
      @apply text-gray-600;
    }
  }

  &__info {
    @apply py-4;

    @apply px-6;
    @screen md {
      @apply px-0;
    }

    &__description {
      &__title {
        @apply text-spl-primary;
        @apply font-bold;

        font-size: 14px;

        @screen lg {
          font-size: 16px;
        }
      }
    }
  }

  &__services-getaways-container {
    @apply flex;
    @apply flex-col-reverse;

    @screen md {
      @apply flex-col;
    }
  }

  &__list-wrapper {
    @apply mt-2;
    @apply py-4;

    @screen lg {
      @apply gap-3;
      @apply grid;
      grid-template-columns: 25% 75%;
    }
  }

  &__mobile-selected-filters {
    @apply px-4 pt-1 pb-4;

    &__row {
      @apply py-1;
    }

    &__category {
      @apply m-1;
    }
  }

  &__mobile-filters {
    &__header {
      &__title {
        @apply text-xl;
        @apply text-spl-dark;
        @apply py-4;
        @apply px-2;
        @apply font-semibold;
      }

      &__controls {
        @apply py-2;
        @apply flex justify-end;
      }
    }

    &__button {
      @apply flex items-center;
      @apply text-sm;

      @apply px-4;
      @apply py-2;
      @apply border-r-1;

      &__icon {
        @apply mr-1;
      }
    }

    &__expanded-filter {
      &__header {
        @apply font-bold;
        @apply py-2;

        @apply text-spl-dark;
      }

      &__checkbox-node {
        input,
        label {
          @apply cursor-pointer;
        }

        label {
          @apply px-2;
          @apply text-spl-dark;
        }
        }

        &__checkbox-node:hover {
        label {
          @apply font-bold;
        }
        }

        &__checkbox-node.selected {
        label {
          @apply font-bold;
        }
      }

      @apply pb-2 mb-2;
    }

    &__sort-selector {
      @apply text-sm;
      @apply px-4;
      @apply py-2;

      &__label {
        @apply font-bold;
        @apply italic;
      }
    }
  }

  &__filters {
    @apply py-4;
    @apply px-4;

    &__title {
      @apply text-lg;
      @apply font-bold;
      @apply text-spl-dark;

      @apply w-full;
      @apply py-1;
      @apply my-1;
      @apply border-b-1;
      @apply border-spl-secondary;
    }

    &__expanded-filter {
      @apply py-2;

      &__header {
        @apply font-bold;
        @apply py-2;

        @apply text-spl-dark;
      }

      &__checkbox-node {

        input,
        label {
          @apply cursor-pointer;
        }

        label {
          @apply px-2;
          @apply text-spl-dark;
        }
      }

      &__checkbox-node:hover {
        label {
          @apply font-bold;
        }
      }

      &__checkbox-node.selected {
        label {
          @apply font-bold;
        }
      }
    }

    &__filter-selector {
      @apply my-2;
    }
  }

  &__service-list {
    &__section-title {
      @apply font-bold;
      @apply text-xl;

      @apply text-spl-primary-dark;

      @apply pb-2;
      @apply mb-4;

      @apply px-4;

      @screen lg {
        @apply px-0;
      }
    }

    &__header {
      @apply hidden;

      @screen lg {
        @apply flex;
        @apply justify-between;
        @apply items-center;
      }

      @apply px-2 pb-1;

      &__subtitle {
        @apply text-spl-dark;
        @apply text-sm;
      }

      &__sort-selector {
        @apply my-2;

        &__label {
          @apply font-bold;
          @apply italic;
        }
      }
    }

    &__no-services {
      @apply text-center;
      @apply py-6;

      @apply mx-2;
      @apply my-8;

      @screen lg {
        @apply my-2;
      }

      @apply border;
      @apply border-spl-gray-1;
      border-radius: 16px;

      &__header {
        @apply font-bold;
        @apply text-spl-acent-dark;
      }
    }
  }

  &__get-aways {
    @apply px-2;

    @screen lg {
      @apply px-0;
    }

    &__title {
      @apply font-bold;
      @apply text-xl;

      @apply text-spl-primary-dark;

      @apply py-2;
      @apply mb-4;

      @apply px-2;

      @screen lg {
        @apply px-0;
      }
    }

    &__body {
      @screen lg {
        @apply gap-3;
        @apply grid;
        grid-template-columns: 25% 75%;
      }

      &__filters {
        @apply overflow-visible;

        @apply py-4;
        @apply px-4;

        @apply mb-6;

        &__title {
          @apply text-lg;
          @apply font-bold;
          @apply text-spl-dark;

          @apply w-full;
          @apply py-1;
          @apply my-1;
          @apply border-b-1;
          @apply border-spl-secondary;
        }

        &__dates {
          @apply pt-4;
          @apply my-2;
        }

        &__pax {
          @apply flex;
          @apply items-center;
          @apply gap-2;

          @apply p-4;

          @apply border-1;
          @apply border-spl-gray-1;
        }

        &__update-btn {
          @apply pt-6;
          @apply pb-4;

          @apply text-right;
        }
      }
    }
  }

  &__location-details {
    @apply py-4;

    &__map {
      @apply px-4;
      height: 267px;
      width: 100%;
      @screen lg {
        height: 377px;
        @apply w-full;
      }
    }

    &__schedule{
      @apply mt-4 mb-4 px-6;
      &__header {
        @apply text-spl-dark;
        font-size: 16px;
        @apply font-bold;
        @apply mt-0 mb-4;
      }

      &__day {
        @apply font-semibold;
        @apply capitalize;
      }
    }

    @screen lg {
      @apply flex flex-row;

      &__schedule{
        min-width: 200px;
        @apply mt-0;
      }
    }
  }

}
</style>
